/**
 * Frontend Encryption Utility using Web Crypto API
 */

/**
 * Imports an RSA public key from a PEM string
 * @param {string} pem - RSA public key in PEM format
 * @returns {Promise<CryptoKey>}
 */
async function importPublicKey(pem) {
  // Remove PEM header and footer
  const pemHeader = "-----BEGIN PUBLIC KEY-----";
  const pemFooter = "-----END PUBLIC KEY-----";
  const pemContents = pem
    .replace(pemHeader, "")
    .replace(pemFooter, "")
    .replace(/\s/g, "");
  
  const binaryDerString = window.atob(pemContents);
  const binaryDer = new Uint8Array(binaryDerString.length);
  for (let i = 0; i < binaryDerString.length; i++) {
    binaryDer[i] = binaryDerString.charCodeAt(i);
  }

  return await window.crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );
}

/**
 * Encrypts data using AES-256-GCM and encrypts the AES key with RSA
 * @param {string} plainText - Data to encrypt
 * @param {string} rsaPublicKeyPem - Backend's RSA public key
 * @returns {Promise<Object>} - { encryptedData, encryptedKey, iv, authTag }
 */
export async function encryptForBackend(plainText, rsaPublicKeyPem) {
  try {
    // 1. Generate a random 256-bit AES key
    const aesKey = await window.crypto.subtle.generateKey(
      {
        name: "AES-GCM",
        length: 256,
      },
      true,
      ["encrypt"]
    );

    // 2. Generate a random IV
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // 3. Encrypt the data with AES-GCM
    const encodedData = new TextEncoder().encode(plainText);
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      aesKey,
      encodedData
    );

    // Web Crypto API includes the auth tag at the end of the encrypted buffer
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const authTag = encryptedArray.slice(-16);
    const dataOnly = encryptedArray.slice(0, -16);

    // 4. Export the AES key to encrypt it with RSA
    const exportedAesKey = await window.crypto.subtle.exportKey("raw", aesKey);

    // 5. Import the RSA public key
    const rsaPublicKey = await importPublicKey(rsaPublicKeyPem);

    // 6. Encrypt the AES key with RSA-OAEP
    const encryptedAesKeyBuffer = await window.crypto.subtle.encrypt(
      {
        name: "RSA-OAEP",
      },
      rsaPublicKey,
      exportedAesKey
    );

    return {
      encryptedData: window.btoa(String.fromCharCode(...dataOnly)),
      encryptedKey: window.btoa(String.fromCharCode(...new Uint8Array(encryptedAesKeyBuffer))),
      iv: window.btoa(String.fromCharCode(...iv)),
      authTag: window.btoa(String.fromCharCode(...authTag)),
    };
  } catch (error) {
    console.error("Encryption failed:", error);
    throw new Error("Failed to encrypt data.");
  }
}
